import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Pencil, Trash2, Power, X, Truck, Boxes } from "lucide-react";
import DataTable from "../../../components/DataTable";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { searchVendors, createVendor, updateVendor, deleteVendor } from "../../../services/materialAdmin";
import VendorCoverageDrawer from "./VendorCoverageDrawer";

const EMPTY = {
  vendorCode: "", vendorName: "", vendorType: "MATERIAL", contactPerson: "", email: "", phone: "",
  gstNumber: "", websiteUrl: "",
  // MOVE_IN only
  servesLocal: false, servesIntercity: false, baseCity: "", baseState: "", basePincode: "",
};

const Field = ({ label, value, onChange, required, placeholder }) => (
  <div>
    <label className="text-xs font-medium text-slate-600">
      {label} {required && <span className="text-flamingo-600">*</span>}
    </label>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-xs font-medium text-slate-600">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const CheckField = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500/40" />
    {label}
  </label>
);

const VendorsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // vendor row being edited
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // {id, name}
  const [coverageVendor, setCoverageVendor] = useState(null); // vendor whose coverage drawer is open

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await searchVendors({ isActive: null });
      setRows(res?.items || (Array.isArray(res) ? res : []));
    } catch (e) {
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      vendorCode: v.vendorCode, vendorName: v.vendorName, vendorType: v.vendorType || "MATERIAL",
      contactPerson: v.contactPerson, email: v.email, phone: v.phone, gstNumber: v.gstNumber,
      websiteUrl: v.websiteUrl,
      servesLocal: !!v.servesLocal, servesIntercity: !!v.servesIntercity,
      baseCity: v.baseCity || "", baseState: v.baseState || "", basePincode: v.basePincode || "",
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.vendorName?.trim() || (!editing && !form.vendorCode?.trim())) {
      return toast.error("Vendor code and name are required");
    }
    setSaving(true);
    try {
      if (editing) {
        const { vendorCode, ...rest } = form; // code is immutable on edit
        await updateVendor(editing.vendorId, rest);
        toast.success("Vendor updated");
      } else {
        await createVendor(form);
        toast.success("Vendor created");
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save vendor");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (v) => {
    try {
      await updateVendor(v.vendorId, { isActive: !v.isActive });
      toast.success(v.isActive ? "Vendor deactivated" : "Vendor activated");
      await load();
    } catch (e) {
      toast.error("Failed to update vendor status");
    }
  };

  const doDelete = async () => {
    const id = confirm?.id;
    setConfirm(null);
    try {
      await deleteVendor(id);
      toast.success("Vendor deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete vendor");
    }
  };

  const columns = [
    { key: "name", header: "Vendor", width: 240, render: (r) => (
      <div className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{r.vendorName}</p>
          {r.vendorType === "MOVE_IN" && (
            <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">Move-in</span>
          )}
        </div>
        <p className="text-xs text-slate-500">{r.vendorCode}</p>
      </div>
    )},
    { key: "contact", header: "Contact", width: 220, render: (r) => (
      <div className="min-w-0">
        <p className="text-sm text-slate-700 truncate">{r.contactPerson || "—"}</p>
        <p className="text-xs text-slate-500 truncate">{r.email || r.phone || "—"}</p>
      </div>
    )},
    { key: "gst", header: "GST", width: 150, render: (r) => <span className="text-sm text-slate-600">{r.gstNumber || "—"}</span> },
    { key: "status", header: "Status", width: 120, render: (r) => (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${r.isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${r.isActive ? "bg-emerald-500" : "bg-rose-500"}`} />
        {r.isActive ? "ACTIVE" : "INACTIVE"}
      </span>
    )},
    { key: "actions", header: "Actions", width: 180, align: "center", render: (r) => (
      <div className="flex items-center justify-center gap-1.5">
        {r.vendorType !== "MOVE_IN" && (
          <button title="Manage coverage" onClick={() => setCoverageVendor(r)} className="w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 inline-flex items-center justify-center">
            <Boxes size={15} />
          </button>
        )}
        <button title="Edit" onClick={() => openEdit(r)} className="w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 inline-flex items-center justify-center">
          <Pencil size={15} />
        </button>
        <button title={r.isActive ? "Deactivate" : "Activate"} onClick={() => toggleActive(r)} className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${r.isActive ? "text-amber-500 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}`}>
          <Power size={15} />
        </button>
        <button title="Delete" onClick={() => setConfirm({ id: r.vendorId, name: r.vendorName })} className="w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center">
          <Trash2 size={15} />
        </button>
      </div>
    )},
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Suppliers who provide materials to the platform.</p>
        <button onClick={openAdd} className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add Vendor
        </button>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        minWidth={880}
        rowKey={(r, i) => r?.vendorId ?? i}
        emptyState={
          <div className="px-6 py-12 text-center">
            <Truck size={28} className="mx-auto text-slate-300" />
            <p className="text-base font-semibold text-slate-900 mt-2">No vendors yet</p>
            <p className="text-sm text-slate-500 mt-1">Add a vendor, then set the sub-categories and brands they cover.</p>
          </div>
        }
      />

      {/* Add/Edit modal */}
      <div className={`fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 ${modalOpen ? "" : "hidden"}`}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="text-base font-semibold text-slate-900">{editing ? "Edit Vendor" : "Add Vendor"}</h3>
            <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Vendor Code" value={form.vendorCode} onChange={(v) => setForm({ ...form, vendorCode: v })} required placeholder="e.g. GREENPLY-MUM" />
            <Field label="Vendor Name" value={form.vendorName} onChange={(v) => setForm({ ...form, vendorName: v })} required placeholder="Greenply Mumbai" />
            <SelectField
              label="Type"
              value={form.vendorType}
              onChange={(v) => setForm({ ...form, vendorType: v })}
              options={[{ value: "MATERIAL", label: "Material supplier" }, { value: "MOVE_IN", label: "Move-in service" }]}
            />
            <Field label="Contact Person" value={form.contactPerson} onChange={(v) => setForm({ ...form, contactPerson: v })} />
            <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <Field label="GST Number" value={form.gstNumber} onChange={(v) => setForm({ ...form, gstNumber: v })} />
            <Field label="Website" value={form.websiteUrl} onChange={(v) => setForm({ ...form, websiteUrl: v })} />

            {form.vendorType === "MOVE_IN" && (
              <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <p className="text-xs font-semibold text-slate-600 mb-3">Move-in service coverage</p>
                <div className="flex flex-wrap gap-5 mb-4">
                  <CheckField label="Serves local moves" checked={form.servesLocal} onChange={(v) => setForm({ ...form, servesLocal: v })} />
                  <CheckField label="Serves intercity moves" checked={form.servesIntercity} onChange={(v) => setForm({ ...form, servesIntercity: v })} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Base City" value={form.baseCity} onChange={(v) => setForm({ ...form, baseCity: v })} placeholder="Pune" />
                  <Field label="Base State" value={form.baseState} onChange={(v) => setForm({ ...form, baseState: v })} placeholder="Maharashtra" />
                  <Field label="Base Pincode" value={form.basePincode} onChange={(v) => setForm({ ...form, basePincode: v })} placeholder="411060" />
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
            <button onClick={() => setModalOpen(false)} className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2.5 hover:bg-white">Cancel</button>
            <button onClick={save} disabled={saving} className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!confirm}
        onConfirm={doDelete}
        onCancel={() => setConfirm(null)}
        message={`Delete vendor "${confirm?.name}"? This removes its sub-category coverage and brands.`}
      />

      {coverageVendor && (
        <VendorCoverageDrawer vendor={coverageVendor} onClose={() => setCoverageVendor(null)} />
      )}
    </div>
  );
};

export default VendorsTab;
